// In-app-browser auto-escape paused until detection is reliable (it kept
// missing real WhatsApp builds — see inAppBrowser.ts/inAppBrowserEscape.ts).
// Google login for buyers is disabled instead for now (BuyerLoginPage.tsx /
// BuyerRegisterPage.tsx), which sidesteps the whole problem. Re-add this
// import to resume the escape attempt once detection is solved.
// import './lib/inAppBrowserEscape';
import './lib/earlyBuyerOAuthCapture';
import './lib/earlyErpOAuthCapture';
import './lib/earlyMercadoPagoOAuthCapture';
import './i18n/config';
import { createRoot } from 'react-dom/client';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import App from './App.tsx';
import './index.css';
import { validateSession } from '@/lib/auth/simpleAuth';
import { CustomDomainProvider } from '@/contexts/CustomDomainContext';

// After a new deploy, a tab left open from before it can still try to lazy-load
// a JS chunk whose hashed filename no longer exists in the current build. That
// throws here as a plain error, not something worth showing the user — a single
// reload fetches the current index.html and fixes it. Bounded to one silent
// retry per tab (via sessionStorage) so a genuinely broken chunk still falls
// through to the manual "Recarregar" button instead of reload-looping forever.
const CHUNK_RELOAD_KEY = 'vt_chunk_reload_attempted';

function isChunkLoadError(message: string): boolean {
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(message);
}

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; message: string }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);

    if (isChunkLoadError(error.message) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: 'system-ui, sans-serif',
          background: '#f8fafc',
        }}>
          <div style={{
            maxWidth: '500px',
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            textAlign: 'center',
          }}>
            <h1 style={{ color: '#dc2626', marginBottom: '20px' }}>Algo deu errado</h1>
            <p style={{ marginBottom: '20px', color: '#374151', fontSize: '14px', wordBreak: 'break-word' }}>
              {this.state.message || 'Erro inesperado na aplicação.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Environment validation before app initialization
const validateEnvironment = () => {
  const requiredEnvVars = {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };

  const missing = Object.entries(requiredEnvVars)
    .filter(([key, value]) => !value || value === 'undefined')
    .map(([key]) => key);
  if (missing.length > 0) {
    console.error('❌ MISSING ENVIRONMENT VARIABLES:', missing);
    console.error('Available env vars:', Object.keys(import.meta.env).filter(k => k.startsWith('VITE_')));
    
    // Show error in DOM instead of throwing
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `
        <div style="
          min-height: 100vh; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          padding: 20px;
          font-family: system-ui, sans-serif;
          background: #f8fafc;
        ">
          <div style="
            max-width: 500px; 
            background: white; 
            padding: 30px; 
            border-radius: 8px; 
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            text-align: center;
          ">
            <h1 style="color: #dc2626; margin-bottom: 20px;">⚠️ Erro de Configuração</h1>
            <p style="margin-bottom: 20px; color: #374151;">
              As variáveis de ambiente do Supabase não estão configuradas corretamente.
            </p>
            <p style="margin-bottom: 20px; color: #6b7280; font-size: 14px;">
              Variáveis faltando: ${missing.join(', ')}
            </p>
            <button 
              onclick="window.location.reload()" 
              style="
                background: #3b82f6; 
                color: white; 
                border: none; 
                padding: 10px 20px; 
                border-radius: 6px; 
                cursor: pointer;
              "
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      `;
    }
    return false;
  }
  
  console.log('✅ Environment validation passed');
  return true;
};
// Only initialize app if environment is valid
if (validateEnvironment()) {
  // Initialize session validation
  validateSession();
  
  createRoot(document.getElementById('root')!).render(
    <ErrorBoundary>
      <CustomDomainProvider>
        <App />
      </CustomDomainProvider>
    </ErrorBoundary>
  );
}